// Runtime queries over a decoded composition (docs/specs/2026-09-23-tycoon-partb-interfaces.md): directed path
// arclength → corridor arclength through affine spans, ramp height h(c), static sample suppression, the
// corridor-owned ramp sampling stream, and crossing-zone membership. Pure and immutable after construction.
import type { Composition, Corridor, CorridorProperty, Ramp, Suppression, Variant, VariantSpan, XY } from './composition.js';
import type { CrossingStrata } from './precedence.js';

/** Default profile grade: rise 2 height units over 240 m. */
export const RAMP_GRADE = 2 / 240;
/** Ramp module pitch (and corridor-stream sample pitch), metres. */
export const RAMP_SAMPLE_PITCH_M = 10;
/** Module centres sit at c ≡ 5 (mod 10) on every corridor. */
export const LATTICE_PHASE_M = 5;

export interface PathMapping { corridorId: string; c: number; direction: 1 | -1 }
export interface RampSample { rampId: string; corridorId: string; profileId: string; c: number; e: number; n: number; heading: number; height: number; module: number; modules: number; slope: 1 | -1 }
export interface StreamSample { corridorId: string; c: number; e: number; n: number; heading: number; height: number; alignment: import('./contracts.js').Alignment }
export interface CompositionIndex {
  readonly composition: Composition;
  mapPath(pathId: string, s: number): PathMapping | undefined;
  propertyAt(corridorId: string, c: number): CorridorProperty | undefined;
  rampAt(corridorId: string, c: number): Ramp | undefined;
  /** Ramp height h(c) inside a ramp, otherwise the corridor property height. */
  heightAt(corridorId: string, c: number): number | undefined;
  suppressed(pathId: string, s: number): boolean;
  /** Every corridor lattice slot outside a ramp, at the corridor geometry and profile (static drawing is corridor-only). */
  corridorSamples(): readonly StreamSample[];
  /** Corridor station intervals listing `stationId`, in corridor-id order. */
  stationIntervals(stationId: string): readonly { corridorId: string; range: readonly [number, number] }[];
  corridor(corridorId: string): Corridor | undefined;
  /** Arclength of the corridor point nearest (e, n), searching within ±`window` metres of `near`. */
  arclengthNear(corridorId: string, e: number, n: number, near: number, window?: number): number | undefined;
  rampSamples(): readonly RampSample[];
  /** Corridor-owned samples for `corridorStreams`, on the corridor lattice, at the corridor profile alignment and height. */
  streamSamples(): readonly StreamSample[];
  strata(): readonly CrossingStrata[];
  /** Crossing ids whose zone intersects `footprint` (world metres) and in which `corridorId` is a stratum. */
  crossingsFor(corridorId: string | null, footprint: readonly XY[]): string[];
  pointAt(corridorId: string, c: number): { e: number; n: number; heading: number } | undefined;
}

const EPSILON = 1e-9;

/** Affine map inside one span; spans are half-open except the variant's last, which includes the path end. */
const mapSpan = (span: VariantSpan, s: number): number =>
  span.corridorRange[0] + (s - span.sourceRange[0]) * (span.corridorRange[1] - span.corridorRange[0]) / (span.sourceRange[1] - span.sourceRange[0]);
function spanAt(variant: Variant, s: number): VariantSpan | undefined {
  const spans = variant.spans;
  if (s < spans[0]!.sourceRange[0] - EPSILON || s > variant.pathLength + EPSILON) return undefined;
  let lo = 0, hi = spans.length - 1;
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (spans[mid]!.sourceRange[0] <= s) lo = mid; else hi = mid - 1; }
  return spans[lo];
}

/** Separating-axis test for two convex polygons with positive-area overlap. */
function convexOverlap(a: readonly XY[], b: readonly XY[]): boolean {
  for (const polygon of [a, b]) for (let i = 0; i < polygon.length; i += 1) {
    const p = polygon[i]!, q = polygon[(i + 1) % polygon.length]!, nx = q[1] - p[1], ny = p[0] - q[0];
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for (const v of a) { const d = v[0] * nx + v[1] * ny; minA = Math.min(minA, d); maxA = Math.max(maxA, d); }
    for (const v of b) { const d = v[0] * nx + v[1] * ny; minB = Math.min(minB, d); maxB = Math.max(maxB, d); }
    const scale = Math.hypot(nx, ny) * 1e-6;
    if (maxA <= minB + scale || maxB <= minA + scale) return false;
  }
  return true;
}

function corridorPoint(corridor: Corridor, c: number): { e: number; n: number; heading: number } {
  const s = corridor.sMeters, clamped = Math.max(0, Math.min(s.at(-1)!, c));
  let i = 1; while (i < s.length - 1 && s[i]! < clamped) i += 1;
  const a = corridor.points[i - 1]!, b = corridor.points[i]!, f = (clamped - s[i - 1]!) / (s[i]! - s[i - 1]!);
  // Tile-space heading: tile x is east, tile y is south (projection.ts).
  return { e: a[0] + (b[0] - a[0]) * f, n: a[1] + (b[1] - a[1]) * f, heading: Math.atan2(-(b[1] - a[1]), b[0] - a[0]) };
}

/** Corridor lattice centres (c = 10k + 5) in [from, to). */
export function latticeIn(from: number, to: number): number[] {
  const result: number[] = [];
  for (let c = Math.ceil((from - LATTICE_PHASE_M) / RAMP_SAMPLE_PITCH_M - EPSILON) * RAMP_SAMPLE_PITCH_M + LATTICE_PHASE_M; c < to - EPSILON; c += RAMP_SAMPLE_PITCH_M) result.push(c);
  return result;
}
export function createCompositionIndex(composition: Composition): CompositionIndex {
  const variants = new Map(composition.variants.map((variant) => [variant.pathId, variant]));
  const corridors = new Map(composition.corridors.map((corridor) => [corridor.id, corridor]));
  const rampsByCorridor = new Map<string, Ramp[]>();
  for (const ramp of composition.ramps) rampsByCorridor.set(ramp.corridorId, [...(rampsByCorridor.get(ramp.corridorId) ?? []), ramp]);
  const suppressions = new Map<string, Suppression[]>();
  for (const item of composition.suppressedSamples) suppressions.set(item.pathId, [...(suppressions.get(item.pathId) ?? []), item]);
  const strata = composition.crossings.map((crossing) => ({ id: crossing.id, lower: crossing.strata[0], upper: crossing.strata[1] }));
  const profiles = new Map(composition.rampProfiles.map((profile) => [profile.id, profile]));
  const rampAt = (corridorId: string, c: number) => rampsByCorridor.get(corridorId)?.find((ramp) => c >= ramp.start - EPSILON && c <= ramp.end + EPSILON);
  const propertyAt = (corridorId: string, c: number) => {
    const properties = corridors.get(corridorId)?.properties;
    if (!properties) return undefined;
    let found = properties[0]!;
    for (const property of properties) { if (property.start > c + EPSILON) break; found = property; }
    return found;
  };
  const rampHeight = (ramp: Ramp, c: number) => ramp.h0 + (ramp.h1 - ramp.h0) * Math.max(0, Math.min(1, (c - ramp.start) / (ramp.end - ramp.start)));
  // One sampling phase per corridor (orchestrator decision 2026-09-24): every static module is centred on the corridor
  // lattice c = 10k + 5, so ramp, stream and per-path modules abut at every handover. A ramp owns the lattice centres in
  // [start, end); an end module may straddle its endpoint by < 5 m (≤ grade · 5 m ≈ 0.04 units of step).
  const rampSamples: RampSample[] = composition.ramps.flatMap((ramp) => {
    const corridor = corridors.get(ramp.corridorId)!, centres = latticeIn(ramp.start, ramp.end);
    return centres.map((c, k) => ({ rampId: ramp.id, corridorId: ramp.corridorId, profileId: ramp.profileId, c, ...corridorPoint(corridor, c), height: rampHeight(ramp, c), module: k, modules: centres.length, slope: ramp.h1 >= ramp.h0 ? 1 as const : -1 as const }));
  });
  Object.freeze(rampSamples);
  const streamSamples: StreamSample[] = composition.corridorStreams.flatMap((stream) => {
    const corridor = corridors.get(stream.corridorId)!;
    return latticeIn(Math.min(...stream.range), Math.max(...stream.range)).map((c) => {
      const property = propertyAt(stream.corridorId, c)!;
      return { corridorId: stream.corridorId, c, ...corridorPoint(corridor, c), height: property.height, alignment: property.alignment };
    });
  });
  Object.freeze(streamSamples);
  const corridorSamples: StreamSample[] = composition.corridors.flatMap((corridor) => latticeIn(0, corridor.sMeters.at(-1)!)
    .filter((c) => !(rampsByCorridor.get(corridor.id) ?? []).some((ramp) => c >= ramp.start - EPSILON && c < ramp.end - EPSILON))
    .map((c) => { const property = propertyAt(corridor.id, c)!; return { corridorId: corridor.id, c, ...corridorPoint(corridor, c), height: property.height, alignment: property.alignment }; }));
  Object.freeze(corridorSamples);
  const stationIntervals = new Map<string, { corridorId: string; range: readonly [number, number] }[]>();
  for (const corridor of [...composition.corridors].sort((a, b) => (a.id < b.id ? -1 : 1))) for (const station of corridor.stations) stationIntervals.set(station.stationId, [...(stationIntervals.get(station.stationId) ?? []), { corridorId: corridor.id, range: station.range }]);
  return {
    composition,
    mapPath(pathId, s) {
      const variant = variants.get(pathId), span = variant && spanAt(variant, s);
      return span ? { corridorId: span.corridorId, c: mapSpan(span, s), direction: span.direction } : undefined;
    },
    propertyAt,
    rampAt,
    heightAt(corridorId, c) { const ramp = rampAt(corridorId, c); return ramp ? rampHeight(ramp, c) : propertyAt(corridorId, c)?.height; },
    suppressed: (pathId, s) => (suppressions.get(pathId) ?? []).some((item) => s >= item.sourceRange[0] - EPSILON && s < item.sourceRange[1] - EPSILON),
    arclengthNear(corridorId, e, n, near, window = 40) {
      const corridor = corridors.get(corridorId);
      if (!corridor) return undefined;
      const sm = corridor.sMeters, pts = corridor.points;
      let best = { c: near, d: Infinity };
      for (let i = 1; i < sm.length; i += 1) {
        if (sm[i]! < near - window || sm[i - 1]! > near + window) continue;
        const [x0, y0] = pts[i - 1]!, [x1, y1] = pts[i]!, dx = x1 - x0, dy = y1 - y0, length2 = dx * dx + dy * dy;
        const f = length2 === 0 ? 0 : Math.max(0, Math.min(1, ((e - x0) * dx + (n - y0) * dy) / length2)), d = Math.hypot(e - x0 - f * dx, n - y0 - f * dy);
        if (d < best.d) best = { c: sm[i - 1]! + f * (sm[i]! - sm[i - 1]!), d };
      }
      return best.d === Infinity ? undefined : best.c;
    },
    corridorSamples: () => corridorSamples,
    stationIntervals: (stationId) => stationIntervals.get(stationId) ?? [],
    corridor: (corridorId) => corridors.get(corridorId),
    rampSamples: () => rampSamples,
    streamSamples: () => streamSamples,
    strata: () => strata,
    crossingsFor(corridorId, footprint) {
      if (corridorId === null) return [];
      return composition.crossings.filter((crossing) => crossing.strata.includes(corridorId) && crossing.zone.some((polygon) => convexOverlap(polygon, footprint))).map((crossing) => crossing.id);
    },
    pointAt(corridorId, c) { const corridor = corridors.get(corridorId); return corridor ? corridorPoint(corridor, c) : undefined; },
  };
}

